// FastAPI answers a failed request validation with 422 and a `detail` that is a
// LIST of {type, loc, msg, input, ctx} objects, not a string. About 130 call
// sites pass `err.response.data.detail` straight to toast.error() or into JSX,
// and React cannot render plain objects, so one bad form value crashed the
// whole page with "Minified React error #31" (error report, 2026-10-24).
//
// The interceptor below turns such a list into one readable string before any
// component sees it, so every existing call site is fixed at once. The
// original list stays available as `detail_items`. Only arrays are rewritten:
// object details such as {code: "quota_exceeded", ...} are read by gates
// (billing, licence) and are left as they are.

import { translateApiError } from './apiErrorTexts';

const LOC_PREFIXES = new Set(['body', 'query', 'path', 'header', 'cookie']);

function formatLoc(loc) {
  if (!Array.isArray(loc)) return '';
  const parts = loc.filter((p, i) => !(i === 0 && LOC_PREFIXES.has(String(p))));
  return parts.map((p) => String(p)).join('.');
}

function formatItem(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item !== 'object') return String(item);
  const msg = typeof item.msg === 'string' ? item.msg : typeof item.message === 'string' ? item.message : '';
  const where = formatLoc(item.loc);
  if (msg && where) return `${where}: ${msg}`;
  if (msg) return msg;
  try {
    return JSON.stringify(item);
  } catch (_) {
    return String(item);
  }
}

/** Readable text for a FastAPI error `detail` of any shape. */
export function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const text = detail.map(formatItem).filter(Boolean).join('; ');
    return text || 'Invalid request';
  }
  return formatItem(detail);
}

/** Rewrite array `detail` values on failed responses into a string, in place. */
export function normalizeApiErrorDetail(error) {
  const data = error && error.response && error.response.data;
  if (data && typeof data === 'object' && Array.isArray(data.detail)) {
    data.detail_items = data.detail;
    data.detail = formatApiErrorDetail(data.detail);
  }
  // Plan stage 5: a known server error is shown as a plain sentence in the
  // client's language; the original stays in `detail_raw`.
  if (data && typeof data === 'object' && typeof data.detail === 'string') {
    const plain = translateApiError(data.detail);
    if (plain) {
      data.detail_raw = data.detail;
      data.detail = plain;
    }
  }
  return error;
}

let installed = false;

export function installApiErrorDetailInterceptor(axiosInstance) {
  if (installed || !axiosInstance || !axiosInstance.interceptors) return;
  installed = true;
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(normalizeApiErrorDetail(error))
  );
}
